import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { apiUrl } from '@/lib/api'

export interface BreadcrumbItem {
  label: string
  path: string
  isActive?: boolean
}

interface BreadcrumbContextType {
  breadcrumbs: BreadcrumbItem[]
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void
  addBreadcrumb: (item: BreadcrumbItem) => void
  clearBreadcrumbs: () => void
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined)

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbContext)
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider')
  }
  return context
}

interface BreadcrumbProviderProps {
  children: ReactNode
}

export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([])
  const location = useLocation()
  const params = useParams()
  const isGeneratingRef = useRef(false)

  // Memoize the generateBreadcrumbs function to prevent infinite loops
  const generateBreadcrumbs = useCallback(async () => {
    if (isGeneratingRef.current) return
    isGeneratingRef.current = true

    try {
      const pathSegments = location.pathname.split('/').filter(Boolean)
      const newBreadcrumbs: BreadcrumbItem[] = []

      // Always start with Dashboard
      newBreadcrumbs.push({
        label: 'Dashboard',
        path: '/',
        isActive: location.pathname === '/'
      })

      // Handle different route patterns
      if (pathSegments.length === 0) {
        // We're at dashboard, already handled above
      } else if (pathSegments[0] === 'tenants') {
        newBreadcrumbs.push({
          label: 'Tenants',
          path: '/tenants',
          isActive: location.pathname === '/tenants'
        })

        // If we have a tenant ID, fetch tenant name and add to breadcrumbs
        if (pathSegments[1] && pathSegments[1] !== 'new') {
          try {
            const { auth } = await import('@/lib/firebase')
            const token = await auth.currentUser?.getIdToken()
            if (token) {
              const response = await fetch(apiUrl(`/api/admin/tenants/${pathSegments[1]}`), {
                headers: { 'Authorization': `Bearer ${token}` }
              })
              if (response.ok) {
                const tenant = await response.json()
                newBreadcrumbs.push({
                  label: tenant.name,
                  path: `/tenants/${pathSegments[1]}`,
                  isActive: location.pathname === `/tenants/${pathSegments[1]}`
                })
              } else {
                // Fallback to tenant ID if name fetch fails
                newBreadcrumbs.push({
                  label: `Tenant ${pathSegments[1]}`,
                  path: `/tenants/${pathSegments[1]}`,
                  isActive: location.pathname === `/tenants/${pathSegments[1]}`
                })
              }
            }
          } catch (error) {
            // Fallback to tenant ID if name fetch fails
            newBreadcrumbs.push({
              label: `Tenant ${pathSegments[1]}`,
              path: `/tenants/${pathSegments[1]}`,
              isActive: location.pathname === `/tenants/${pathSegments[1]}`
            })
          }

          // Handle sub-pages under tenant
          if (pathSegments[2]) {
            const subPage = pathSegments[2]
            let subPageLabel = subPage.charAt(0).toUpperCase() + subPage.slice(1)
            
            // Special handling for specific sub-pages
            if (subPage === 'users') {
              subPageLabel = 'Users'
            } else if (subPage === 'roles') {
              subPageLabel = 'Roles & Permissions'
            }

            newBreadcrumbs.push({
              label: subPageLabel,
              path: `/tenants/${pathSegments[1]}/${subPage}`,
              isActive: true
            })
          }
        }
      } else if (pathSegments[0] === 'plans') {
        newBreadcrumbs.push({
          label: 'Plans',
          path: '/plans',
          isActive: location.pathname === '/plans'
        })

        // Handle plan edit page
        if (pathSegments[1] && pathSegments[2] === 'edit') {
          try {
            const { auth } = await import('@/lib/firebase')
            const token = await auth.currentUser?.getIdToken()
            if (token) {
              const response = await fetch(apiUrl(`/api/admin/plans/${pathSegments[1]}`), {
                headers: { 'Authorization': `Bearer ${token}` }
              })
              if (response.ok) {
                const plan = await response.json()
                newBreadcrumbs.push({
                  label: `Edit ${plan.name}`,
                  path: `/plans/${pathSegments[1]}/edit`,
                  isActive: true
                })
              } else {
                newBreadcrumbs.push({
                  label: 'Edit Plan',
                  path: `/plans/${pathSegments[1]}/edit`,
                  isActive: true
                })
              }
            }
          } catch (error) {
            newBreadcrumbs.push({
              label: 'Edit Plan',
              path: `/plans/${pathSegments[1]}/edit`,
              isActive: true
            })
          }
        }
      } else if (pathSegments[0] === 'subscriptions') {
        newBreadcrumbs.push({
          label: 'Subscriptions',
          path: '/subscriptions',
          isActive: true
        })
      } else if (pathSegments[0] === 'system-settings') {
        newBreadcrumbs.push({
          label: 'System Settings',
          path: '/system-settings',
          isActive: true
        })
      } else if (pathSegments[0] === 'audit-log') {
        newBreadcrumbs.push({
          label: 'Audit Log',
          path: '/audit-log',
          isActive: true
        })
      } else {
        // Handle other top-level pages
        const pageName = pathSegments[0].charAt(0).toUpperCase() + pathSegments[0].slice(1).replace(/-/g, ' ')
        newBreadcrumbs.push({
          label: pageName,
          path: `/${pathSegments[0]}`,
          isActive: true
        })
      }

      setBreadcrumbs(newBreadcrumbs)
    } finally {
      isGeneratingRef.current = false
    }
  }, [location.pathname])

  // Auto-generate breadcrumbs based on current route
  useEffect(() => {
    generateBreadcrumbs()
  }, [generateBreadcrumbs])

  const addBreadcrumb = (item: BreadcrumbItem) => {
    setBreadcrumbs(prev => [...prev, item])
  }

  const clearBreadcrumbs = () => {
    setBreadcrumbs([])
  }

  return (
    <BreadcrumbContext.Provider value={{
      breadcrumbs,
      setBreadcrumbs,
      addBreadcrumb,
      clearBreadcrumbs
    }}>
      {children}
    </BreadcrumbContext.Provider>
  )
}
